import {
  OutboundProxy,
  OutboundProxyPostRequest,
  OutboundProxyPutRequest,
  OutboundRoute,
  OutboundRoutePostRequest,
  OutboundRoutePutRequest,
  VoiceRegion,
  VoiceRegionPostRequest,
  VoiceRegionPutRequest,
  VoiceServer,
  VoiceServerPostRequest,
  VoiceServerPutRequest,
} from "callable-data-objects";
import { GoogleASRLanguage } from "callable-data-objects/dist/enums/GoogleASRLanguage";
import { GoogleTTSLanguage } from "callable-data-objects/dist/enums/GoogleTTSLanguage";
import { APIVoiceRegionContext } from "Providers/APIContexts";
import { APIVoiceRegionProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";

const APIVoiceRegionProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [loading, setLoading] = useState<boolean>(false);

  //Voice Region
  const [voiceRegions, setVoiceRegions] = useState<VoiceRegion[]>([]);
  const [voiceRegion, setVoiceRegion] = useState<VoiceRegion | undefined>();
  const [create, setCreate] = useState<VoiceRegionPostRequest | undefined>();

  //Proxies
  const [outboundProxy, setOutboundProxy] =
    useState<OutboundProxy | undefined>();
  const [createOutboundProxy, setCreateOutboundProxy] =
    useState<OutboundProxyPostRequest | undefined>();

  //Routes
  const [outboundRoute, setOutboundRoute] =
    useState<OutboundRoute | undefined>();
  const [createOutboundRoute, setCreateOutboundRoute] =
    useState<OutboundRoutePostRequest | undefined>();

  //Servers
  const [voiceServer, setVoiceServer] = useState<VoiceServer | undefined>();
  const [createVoiceServer, setCreateVoiceServer] =
    useState<VoiceServerPostRequest | undefined>();

  async function list() {
    setLoading(true);
    const response = await api.regionService.list();
    setVoiceRegions(response);
    setLoading(false);
  }

  useEffect(() => {
    list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  function select(id: string): void {
    setLoading(true);
    api.regionService.get(id).then((resp: VoiceRegion) => {
      setVoiceRegion(resp);
      uncreate();
      setOutboundRoute(undefined);
      setOutboundProxy(undefined);
      setVoiceServer(undefined);
      setLoading(false);
    });
  }

  function selectOutboundProxy(id: string): void {
    if (voiceRegion) {
      const r = voiceRegion.proxies.find((proxy) => proxy.id === id);
      setOutboundProxy(r);
    }
  }

  function selectOutboundRoute(id: string): void {
    if (voiceRegion) {
      const r = voiceRegion.routes.find((route) => route.id === id);
      setOutboundRoute(r);
    }
  }

  function selectVoiceServer(id: string): void {
    if (voiceRegion) {
      const r = voiceRegion.voiceServers.find((server) => server.id === id);
      setVoiceServer(r);
    }
  }

  function unselect() {
    setVoiceRegion(undefined);
    setOutboundProxy(undefined);
    setOutboundRoute(undefined);
    setVoiceServer(undefined);
  }

  function unselectOutboundProxy() {
    setOutboundProxy(undefined);
  }

  function unselectOutboundRoute() {
    setOutboundRoute(undefined);
  }

  function unselectVoiceServer() {
    setVoiceServer(undefined);
  }

  function createVoiceRegion() {
    setCreate({
      name: "",
      description: "",
      ttsLanguage: GoogleTTSLanguage.English_UK_Standard_A_Female,
      asrLanguage: GoogleASRLanguage.English_United_Kingdom,
      dialTimeout: 180,
      dialTimeLimit: 14400,
      teamsDomain: "",
      ringbackURL: "https://vibe-public.s3-eu-west-1.amazonaws.com/en-gb.wav",
      monitoringURL: "",
      monitoringUsername: "",
      monitoringPassword: "",
      applicationURL: "",
      dnsType: "",
      dnsISOCode: "",
    });
  }

  function createVoiceRegionProxy() {
    setCreateOutboundProxy({
      ip: "",
      port: "5060",
      description: "",
      type: "national",
      zone: "eu-west-1",
      priority: 10,
      inbound: true,
      outbound: true,
      active: true,
      countryCode: "GB",
    });
  }

  function createVoiceRegionRoute() {
    setCreateOutboundRoute({
      caller: "*",
      called: "",
      obp: { id: "", name: "" },
    });
  }

  function createVoiceRegionServer() {
    setCreateVoiceServer({
      name: "",
      privateIP: "",
      publicIP: "",
      dns: "",
      teamsProxy: true,
      interconnect: true,
      access: true,
      group: "",
    });
  }

  function uncreate() {
    setCreate(undefined);
    setCreateOutboundProxy(undefined);
    setCreateVoiceServer(undefined);
    setCreateOutboundRoute(undefined);
  }

  function uncreateOutboundProxy() {
    setCreateOutboundProxy(undefined);
  }

  function uncreateOutboundRoute() {
    setCreateOutboundRoute(undefined);
  }

  function uncreateVoiceServer() {
    setCreateVoiceServer(undefined);
  }

  async function save(payload: VoiceRegionPostRequest) {
    if (create) {
      const response = await api.regionService.post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveOutboundProxy(payload: OutboundProxyPostRequest) {
    if (voiceRegion) {
      const response = await api.regionService
        .proxy(voiceRegion.id)
        .post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveOutboundRoute(payload: OutboundRoutePostRequest) {
    if (voiceRegion) {
      const response = await api.regionService
        .route(voiceRegion.id)
        .post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveVoiceServer(payload: VoiceServerPostRequest) {
    if (voiceRegion) {
      const response = await api.regionService
        .server(voiceRegion.id)
        .post("", payload);
      if (response) {
        list();
        select(response.id);
      }
    }
  }

  async function update(payload: VoiceRegionPutRequest) {
    if (voiceRegion) {
      await api.regionService.put(payload.id, payload);
      list();
      unselect();
    }
  }

  async function updateOutboundProxy(payload: OutboundProxyPutRequest) {
    if (voiceRegion) {
      await api.regionService.proxy(voiceRegion.id).put(payload.id, payload);
      list();

      select(voiceRegion.id);
    }
  }

  async function updateOutboundRoute(payload: OutboundRoutePutRequest) {
    if (voiceRegion) {
      await api.regionService.route(voiceRegion.id).put(payload.id, payload);
      list();
      select(voiceRegion.id);
    }
  }

  async function updateVoiceServer(payload: VoiceServerPutRequest) {
    if (voiceRegion) {
      await api.regionService.server(voiceRegion.id).put(payload.id, payload);
      list();
      select(voiceRegion.id);
    }
  }

  async function remove(id: string) {
    await api.regionService.delete(id);
    list();
    unselect();
  }

  async function removeOutboundProxy(id: string) {
    if (voiceRegion) {
      await api.regionService.proxy(voiceRegion.id).delete(id);
      list();
      setOutboundProxy(undefined);
      select(voiceRegion.id);
    }
  }

  async function removeOutboundRoute(id: string) {
    if (voiceRegion) {
      await api.regionService.route(voiceRegion.id).delete(id);
      list();
      setOutboundRoute(undefined);
      select(voiceRegion.id);
    }
  }

  async function removeVoiceServer(id: string) {
    if (voiceRegion) {
      await api.regionService.server(voiceRegion.id).delete(id);
      list();
      setVoiceServer(undefined);
      select(voiceRegion.id);
    }
  }

  const VoiceRegionProps: APIVoiceRegionProps = {
    loading,
    voiceRegion,
    voiceRegions,
    outboundProxy,
    outboundRoute,
    voiceServer,
    create,
    createOutboundProxy,
    createOutboundRoute,
    createVoiceServer,
    createVoiceRegion,
    createVoiceRegionProxy,
    createVoiceRegionRoute,
    createVoiceRegionServer,
    save,
    saveOutboundProxy,
    saveOutboundRoute,
    saveVoiceServer,
    update,
    updateOutboundProxy,
    updateOutboundRoute,
    updateVoiceServer,
    remove,
    removeOutboundProxy,
    removeOutboundRoute,
    removeVoiceServer,
    uncreate,
    uncreateOutboundProxy,
    uncreateOutboundRoute,
    uncreateVoiceServer,
    select,
    selectOutboundProxy,
    selectOutboundRoute,
    selectVoiceServer,
    unselect,
    unselectOutboundProxy,
    unselectOutboundRoute,
    unselectVoiceServer,
  };

  return (
    <APIVoiceRegionContext.Provider value={VoiceRegionProps}>
      {children}
    </APIVoiceRegionContext.Provider>
  );
};

export default APIVoiceRegionProvider;
